import { z } from 'zod';
import { WallClockSchema } from './attendance';
import { PersonRefSchema, SiteRefSchema } from './property';

/**
 * Sales & Customers M1 (docs/specs/sales-customers.md, D78–D82): one definition of sales,
 * credits, margin, working day and target, read from dt_spar by `sales-sync` into Aroo's own
 * read models. Branch page (Today, Month, People, Data health), Targets (monthly per branch,
 * append-only with history), Settings (working-day weights, bands, CDisc exclusions), the Home
 * "My branch today" card and its tasks. Money is £ to the penny, percentages 0–100, dates
 * YYYY-MM-DD, periods YYYY-MM, invoice times London wall clock "YYYY-MM-DDTHH:MM:SS".
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();
const Period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM');
export const PeriodSchema = Period;

export const SALES_PERMISSIONS = {
  branchView: 'sales.branch.view',
  leagueView: 'sales.league.view',
  ownView: 'sales.own.view',
  targetsManage: 'sales.targets.manage',
  settingsManage: 'sales.settings.manage',
  initialsManage: 'sales.initials.manage',
} as const;

export const SalesBandSchema = z.enum([
  'on_track',
  'behind',
  'at_risk',
  'off_track',
  'under',
  'none',
]);
export type SalesBand = z.infer<typeof SalesBandSchema>;
export const SALES_BAND_LABELS: Record<SalesBand, string> = {
  on_track: 'On track',
  behind: 'Behind',
  at_risk: 'At risk',
  off_track: 'Off track',
  under: 'Under target',
  none: 'No target',
};

export const SalesBasisSchema = z.enum(['aroo', 'legacy_cache']);
export type SalesBasis = z.infer<typeof SalesBasisSchema>;

const RegionRef = z.object({ id: z.uuid(), name: z.string() });

export const SalesBranchRefSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  region: RegionRef,
});
export type SalesBranchRef = z.infer<typeof SalesBranchRefSchema>;

export const SalesBranchesResponseSchema = z.object({
  branches: z.array(SalesBranchRefSchema),
  canManageTargets: z.boolean(),
  canManageSettings: z.boolean(),
});
export type SalesBranchesResponse = z.infer<typeof SalesBranchesResponseSchema>;

// Branch › Today ----------------------------------------------------------------------

export const SalesFeedHealthSchema = z.object({
  source: z.string(),
  /** The feed's own LastUpdateTime (London wall clock), null until the first sync. */
  watermark: WallClockSchema.nullable(),
  ageMinutes: z.number().int().nullable(),
  stale: z.boolean(),
  lastRunAt: Instant.nullable(),
  lastOkAt: Instant.nullable(),
  lastError: z.string().nullable(),
});
export type SalesFeedHealth = z.infer<typeof SalesFeedHealthSchema>;

export const SalesHourSchema = z.object({
  hour: z.number().int().min(0).max(23),
  sales: z.number(),
  invoices: z.number().int(),
  yesterdaySales: z.number(),
});

export const SalesTodaySchema = z.object({
  site: SiteRefSchema,
  region: RegionRef,
  date: IsoDate,
  /** Newest invoice-feed watermark (London wall clock), null before the first sync. */
  asOf: WallClockSchema.nullable(),
  stale: z.boolean(),
  today: z.object({
    netSales: z.number(),
    sales: z.number(),
    credits: z.number(),
    creditNotes: z.number().int(),
    invoices: z.number().int(),
    lines: z.number().int(),
    margin: z.number(),
    marginPct: z.number().nullable(),
    lastInvoiceAt: WallClockSchema.nullable(),
    /** Today's target: daily target × today's weight (null without a target or on a 0 day). */
    target: z.number().nullable(),
    targetPct: z.number().nullable(),
    band: SalesBandSchema,
    marginTargetPct: z.number().nullable(),
    marginBand: SalesBandSchema,
    /** Today's weight (1, 0.5 or 0). */
    weight: z.number(),
  }),
  yesterday: z.object({ netSales: z.number(), invoices: z.number().int() }),
  /** 07:00–18:59 buckets; invoices outside them are the "other" figure. */
  hourly: z.array(SalesHourSchema),
  otherHours: z.object({ sales: z.number(), yesterdaySales: z.number() }),
  /** Internal-account (CDisc) invoices left out of the figures today. */
  excluded: z.object({ headers: z.number().int(), goods: z.number() }),
  /** Integrity exceptions today (D88): counted, never removed from the figures. */
  integrity: z.object({ mismatch: z.number().int(), implausible: z.number().int() }),
});
export type SalesToday = z.infer<typeof SalesTodaySchema>;

// Branch › Month ----------------------------------------------------------------------

export const SalesTargetValueSchema = z.object({
  salesTarget: z.number().nullable(),
  marginTargetPct: z.number().nullable(),
  /** No row for this period: the latest earlier period's row applies. */
  inherited: z.boolean(),
  fromPeriod: Period.nullable(),
});
export type SalesTargetValue = z.infer<typeof SalesTargetValueSchema>;

export const WorkingDayWeightsSchema = z.object({
  weekday: z.number(),
  saturday: z.number(),
  sunday: z.number(),
  bankHoliday: z.number(),
});
export type WorkingDayWeights = z.infer<typeof WorkingDayWeightsSchema>;

export const SalesPaceSchema = z.object({
  targetToDate: z.number().nullable(),
  pacePct: z.number().nullable(),
  paceBand: SalesBandSchema,
  projection: z.number().nullable(),
  projectionPct: z.number().nullable(),
  projectionBand: SalesBandSchema,
  /** Net sales per weighted day needed over the days after today. */
  runRate: z.number().nullable(),
  dailyTarget: z.number().nullable(),
  marginBand: SalesBandSchema,
});
export type SalesPace = z.infer<typeof SalesPaceSchema>;

export const SalesMonthDaySchema = z.object({
  date: IsoDate,
  weight: z.number(),
  netSales: z.number().nullable(),
  target: z.number().nullable(),
  pct: z.number().nullable(),
  basis: SalesBasisSchema.nullable(),
});

export const SalesTrendPointSchema = z.object({
  period: Period,
  netSales: z.number().nullable(),
  marginPct: z.number().nullable(),
  target: z.number().nullable(),
  /** 'mixed' when the month has both legacy and Aroo-basis days. */
  basis: z.enum(['aroo', 'legacy_cache', 'mixed']).nullable(),
});

export const SalesMonthSchema = z.object({
  site: SiteRefSchema,
  region: RegionRef,
  period: Period,
  today: IsoDate,
  asOf: WallClockSchema.nullable(),
  target: SalesTargetValueSchema,
  weights: WorkingDayWeightsSchema,
  weightedDays: z.object({
    month: z.number(),
    toDate: z.number(),
    elapsed: z.number(),
    remaining: z.number(),
  }),
  mtd: z.object({
    netSales: z.number(),
    sales: z.number(),
    credits: z.number(),
    creditNotes: z.number().int(),
    cost: z.number(),
    margin: z.number(),
    marginPct: z.number().nullable(),
    invoices: z.number().int(),
  }),
  pace: SalesPaceSchema,
  days: z.array(SalesMonthDaySchema),
  trend: z.array(SalesTrendPointSchema),
  /** First Aroo-basis day, when there are legacy days before it. */
  basisBoundary: IsoDate.nullable(),
});
export type SalesMonth = z.infer<typeof SalesMonthSchema>;

// Branch › People ---------------------------------------------------------------------

export const InitialsDispositionSchema = z.enum(['shared', 'system', 'ignore']);
export type InitialsDisposition = z.infer<typeof InitialsDispositionSchema>;
export const INITIALS_DISPOSITION_LABELS: Record<InitialsDisposition, string> = {
  shared: 'Shared login',
  system: 'System',
  ignore: 'Ignored',
};

export const InitialsDispositionWriteSchema = z.object({
  disposition: InitialsDispositionSchema,
  note: z.string().trim().max(300).optional(),
});
export type InitialsDispositionWrite = z.infer<typeof InitialsDispositionWriteSchema>;

/** One set of header initials at a branch that no person holds, with its disposition if any. */
export const SalesInitialsRowSchema = z.object({
  initials: z.string(),
  invoices: z.number().int(),
  netSales: z.number(),
  disposition: InitialsDispositionSchema.nullable(),
  note: z.string().nullable(),
  decidedBy: PersonRefSchema.nullable(),
  decidedAt: Instant.nullable(),
});
export type SalesInitialsRow = z.infer<typeof SalesInitialsRowSchema>;

export const SalesPersonRowSchema = z.object({
  key: z.string(),
  kind: z.enum(['person', 'shared', 'unassigned']),
  name: z.string(),
  personId: z.uuid().nullable(),
  initials: z.array(z.string()),
  netSales: z.number(),
  sales: z.number(),
  credits: z.number(),
  invoices: z.number().int(),
  marginPct: z.number().nullable(),
  /** Share of the branch's net sales, 0–100. */
  share: z.number().nullable(),
});
export type SalesPersonRow = z.infer<typeof SalesPersonRowSchema>;

export const SalesPeopleSchema = z.object({
  site: SiteRefSchema,
  period: Period,
  rows: z.array(SalesPersonRowSchema),
  total: z.object({ netSales: z.number(), invoices: z.number().int() }),
  canManageInitials: z.boolean(),
  /** Initials this period that match no person at the branch, with their dispositions (D82). */
  initials: z.array(SalesInitialsRowSchema),
});
export type SalesPeople = z.infer<typeof SalesPeopleSchema>;

// Branch › Data health ----------------------------------------------------------------

export const SalesIntegrityKindSchema = z.enum(['mismatch', 'implausible']);
export type SalesIntegrityKind = z.infer<typeof SalesIntegrityKindSchema>;

/** One document whose header disagrees with its lines, or with an implausible line (D88). */
export const SalesIntegrityRowSchema = z.object({
  date: IsoDate,
  prefix: z.string(),
  document: z.string(),
  acct: z.string().nullable(),
  kind: SalesIntegrityKindSchema,
  /** The header's Goods (NULL stored as 0). */
  headerGoods: z.number(),
  /** Σ Qty × Unit over the document's lines. */
  lineValue: z.number(),
  /** Σ Qty × Unit over the document's charged (Status = 'Y') surcharge rows. */
  chargedSurcharges: z.number(),
  maxLineQty: z.number().nullable(),
  firstSeenAt: Instant,
  /** Set when a later re-post made the document agree; the row is kept as history. */
  clearedAt: Instant.nullable(),
});
export type SalesIntegrityRow = z.infer<typeof SalesIntegrityRowSchema>;

export const SalesHealthSchema = z.object({
  site: SiteRefSchema,
  period: Period,
  sources: z.array(SalesFeedHealthSchema),
  month: z.object({
    /** CDisc exclusions this month (count, £), and the split per code. */
    excludedHeaders: z.number().int(),
    excludedGoods: z.number(),
    cdisc: z.array(z.object({ code: z.string(), headers: z.number().int(), goods: z.number() })),
    mismatchHeaders: z.number().int(),
    mismatchGoods: z.number(),
    implausibleHeaders: z.number().int(),
    implausibleGoods: z.number(),
    /** Invoices on branch codes that are not a current site (company total only). */
    unknownBranchInvoices: z.number().int(),
    unknownBranchGoods: z.number(),
    unknownBranchCodes: z.array(z.string()),
    /** S-M3 (D85): invoices this month citing a quote the mirror never received. */
    quotesCitedNotMirrored: z.number().int(),
    quotesCitedNotMirroredDocs: z.array(z.string()),
  }),
  /** Integrity exceptions this month, open first, newest first. */
  exceptions: z.array(SalesIntegrityRowSchema),
  /** The thresholds the check used (Settings). */
  rule: z.object({ integrityTolerance: z.number(), implausibleLineQty: z.number() }),
  basisBoundary: IsoDate.nullable(),
});
export type SalesHealth = z.infer<typeof SalesHealthSchema>;

// Targets -----------------------------------------------------------------------------

export const SalesTargetRowSchema = z.object({
  site: SiteRefSchema,
  region: RegionRef,
  salesTarget: z.number().nullable(),
  marginTargetPct: z.number().nullable(),
  inherited: z.boolean(),
  fromPeriod: Period.nullable(),
  /** Σ weights of the period's days at this branch. */
  weightedDays: z.number(),
  dailyTarget: z.number().nullable(),
  setBy: PersonRefSchema.nullable(),
  setAt: Instant.nullable(),
  note: z.string().nullable(),
});
export type SalesTargetRow = z.infer<typeof SalesTargetRowSchema>;

export const SalesTargetsResponseSchema = z.object({
  period: Period,
  canManage: z.boolean(),
  rows: z.array(SalesTargetRowSchema),
  /** Branches with no row for this period (set or inherited): the Home task's count. */
  notSet: z.number().int(),
});
export type SalesTargetsResponse = z.infer<typeof SalesTargetsResponseSchema>;

export const SalesTargetWriteSchema = z.object({
  siteId: z.uuid(),
  period: Period,
  salesTarget: z.number().min(0).max(99_999_999),
  marginTargetPct: z.number().min(0).max(100),
  note: z.string().trim().max(300).optional(),
});
export type SalesTargetWrite = z.infer<typeof SalesTargetWriteSchema>;

export const SalesTargetsBulkWriteSchema = z.object({
  period: Period,
  rows: z
    .array(
      z.object({
        siteId: z.uuid(),
        salesTarget: z.number().min(0).max(99_999_999),
        marginTargetPct: z.number().min(0).max(100),
      }),
    )
    .min(1)
    .max(200),
  note: z.string().trim().max(300).optional(),
});
export type SalesTargetsBulkWrite = z.infer<typeof SalesTargetsBulkWriteSchema>;

export const SalesTargetHistoryRowSchema = z.object({
  id: z.number().int(),
  period: Period,
  salesTarget: z.number(),
  marginTargetPct: z.number(),
  note: z.string().nullable(),
  setBy: PersonRefSchema.nullable(),
  setAt: Instant,
  supersededAt: Instant.nullable(),
  current: z.boolean(),
});
export const SalesTargetHistorySchema = z.object({
  site: SiteRefSchema,
  rows: z.array(SalesTargetHistoryRowSchema),
});
export type SalesTargetHistory = z.infer<typeof SalesTargetHistorySchema>;

export const SalesOkSchema = z.object({ ok: z.literal(true), written: z.number().int() });
export const SalesOkEmptySchema = z.object({ ok: z.literal(true) });

// Settings ----------------------------------------------------------------------------

export const SalesSettingsChangeSchema = z.object({
  at: Instant,
  actor: PersonRefSchema.nullable(),
  action: z.string(),
  subject: z.string(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
});

export const SalesWeightsRowSchema = z.object({
  site: SiteRefSchema,
  weights: WorkingDayWeightsSchema,
  /** Σ weights of the current month at this branch, so an edit's effect is visible. */
  monthWeightedDays: z.number(),
  updatedBy: PersonRefSchema.nullable(),
  updatedAt: Instant.nullable(),
});

export const SalesSettingsSchema = z.object({
  integrityTolerance: z.number(),
  implausibleLineQty: z.number(),
  paceOnTrackPct: z.number(),
  paceBehindPct: z.number(),
  projectionOnTrackPct: z.number(),
  projectionAtRiskPct: z.number(),
  callsAnswerGreenPct: z.number(),
  callsAnswerAmberPct: z.number(),
  excludeCdisc: z.array(z.string()),
  targetsDueDay: z.number().int(),
  /** "Quotes to chase" (S-M3): open quotes older than this many days, or over this value. */
  quotesChaseDays: z.number().int(),
  quotesChaseValue: z.number(),
  /** Every CDisc code seen this month with the invoices and £ it covers, excluded or not. */
  cdiscThisMonth: z.array(
    z.object({
      code: z.string(),
      headers: z.number().int(),
      goods: z.number(),
      excluded: z.boolean(),
    }),
  ),
  period: Period,
  weights: z.array(SalesWeightsRowSchema),
  updatedBy: PersonRefSchema.nullable(),
  updatedAt: Instant.nullable(),
  changes: z.array(SalesSettingsChangeSchema),
});
export type SalesSettings = z.infer<typeof SalesSettingsSchema>;

const pct = z.number().min(0).max(200);
export const SalesSettingsPatchSchema = z.object({
  integrityTolerance: z.number().min(0).max(100).optional(),
  implausibleLineQty: z.number().int().min(100).max(1_000_000_000).optional(),
  paceOnTrackPct: pct.optional(),
  paceBehindPct: pct.optional(),
  projectionOnTrackPct: pct.optional(),
  projectionAtRiskPct: pct.optional(),
  callsAnswerGreenPct: z.number().min(0).max(100).optional(),
  callsAnswerAmberPct: z.number().min(0).max(100).optional(),
  excludeCdisc: z.array(z.string().trim().min(1).max(10)).max(50).optional(),
  targetsDueDay: z.number().int().min(1).max(28).optional(),
  quotesChaseDays: z.number().int().min(0).max(7).optional(),
  quotesChaseValue: z.number().min(0).max(1_000_000).optional(),
});
export type SalesSettingsPatch = z.infer<typeof SalesSettingsPatchSchema>;

const weight = z.number().min(0).max(1);
export const WorkingDayWeightsWriteSchema = z.object({
  weekday: weight,
  saturday: weight,
  sunday: weight,
  bankHoliday: weight,
});
export type WorkingDayWeightsWrite = z.infer<typeof WorkingDayWeightsWriteSchema>;

// Home --------------------------------------------------------------------------------

export const SalesHomeBranchSchema = z.object({
  site: SiteRefSchema,
  date: IsoDate,
  netSalesToday: z.number(),
  dayTarget: z.number().nullable(),
  dayPct: z.number().nullable(),
  dayBand: SalesBandSchema,
  invoicesToday: z.number().int(),
  mtdNetSales: z.number(),
  targetToDate: z.number().nullable(),
  pacePct: z.number().nullable(),
  paceBand: SalesBandSchema,
  runRate: z.number().nullable(),
  monthTarget: z.number().nullable(),
  asOf: WallClockSchema.nullable(),
  stale: z.boolean(),
  /** 07:00–18:59 net sales today, for the small bars. */
  hourly: z.array(z.object({ hour: z.number().int(), sales: z.number() })),
});
export type SalesHomeBranch = z.infer<typeof SalesHomeBranchSchema>;

// Quotes (S-M3, §3.6, §4.1; D85) ----------------------------------------------------------

export const QuoteStatusSchema = z.enum(['open', 'converted', 'expired']);
export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  open: 'Open',
  converted: 'Converted',
  expired: 'Expired',
};
export const QuoteConversionSchema = z.enum(['full', 'partial', 'over']);
export type QuoteConversion = z.infer<typeof QuoteConversionSchema>;
/** Stock at the quote's branch for its lines: every line covered, some, none; unknown = not checked. */
export const QuoteStockSchema = z.enum(['full', 'partial', 'none', 'unknown']);
export type QuoteStock = z.infer<typeof QuoteStockSchema>;
export const QUOTE_STOCK_LABELS: Record<QuoteStock, string> = {
  full: 'In stock',
  partial: 'Part stock',
  none: 'No stock',
  unknown: 'Not checked',
};

export const SalesQuoteRowSchema = z.object({
  document: z.string(),
  site: SiteRefSchema.nullable(),
  branchCode: z.string(),
  acct: z.string().nullable(),
  customerName: z.string().nullable(),
  initials: z.string(),
  person: PersonRefSchema.nullable(),
  /** London wall clock. */
  createdAt: WallClockSchema,
  ageDays: z.number().int(),
  goods: z.number(),
  lineCount: z.number().int(),
  status: QuoteStatusSchema,
  conversion: QuoteConversionSchema.nullable(),
  convertedAt: WallClockSchema.nullable(),
  invoiceCount: z.number().int(),
  invoicedGoods: z.number(),
  /** Live stock at the branch (open quotes only; 'unknown' when not checked or dt_spar is down). */
  stock: QuoteStockSchema,
  /** Older than the chase threshold or over the chase value (open quotes). */
  chase: z.boolean(),
});
export type SalesQuoteRow = z.infer<typeof SalesQuoteRowSchema>;

export const QuoteConversionRateSchema = z.object({
  days: z.number().int(),
  quotes: z.number().int(),
  converted: z.number().int(),
  pct: z.number().nullable(),
  /** The right edge is inside the 7-day window: the figure can still rise. */
  provisional: z.boolean(),
  quotedGoods: z.number(),
  convertedGoods: z.number(),
});
export type QuoteConversionRate = z.infer<typeof QuoteConversionRateSchema>;

export const SalesQuotePersonRowSchema = z.object({
  key: z.string(),
  kind: z.enum(['person', 'shared', 'unassigned']),
  name: z.string(),
  personId: z.uuid().nullable(),
  initials: z.array(z.string()),
  open: z.number().int(),
  openGoods: z.number(),
  quotes30: z.number().int(),
  converted30: z.number().int(),
  conversionPct: z.number().nullable(),
});
export type SalesQuotePersonRow = z.infer<typeof SalesQuotePersonRowSchema>;

export const SalesQuotesSchema = z.object({
  site: SiteRefSchema,
  today: IsoDate,
  /** The quotes feed's watermark (QHeads), London wall clock. */
  asOf: WallClockSchema.nullable(),
  stale: z.boolean(),
  open: z.object({
    count: z.number().int(),
    goods: z.number(),
    oldestAt: WallClockSchema.nullable(),
    chase: z.number().int(),
  }),
  rates: z.array(QuoteConversionRateSchema),
  byPerson: z.array(SalesQuotePersonRowSchema),
  /** Open quotes, oldest and largest first. */
  rows: z.array(SalesQuoteRowSchema),
  /** Converted and expired quotes raised in the last 14 days, newest first. */
  recent: z.array(SalesQuoteRowSchema),
  /** Stock was checked live for the open quotes (false when dt_spar could not be read). */
  stockChecked: z.boolean(),
  thresholds: z.object({ chaseDays: z.number().int(), chaseValue: z.number() }),
  /** Invoices in the last 30 days that cite a quote the mirror never received. */
  citedNotMirrored30: z.number().int(),
});
export type SalesQuotes = z.infer<typeof SalesQuotesSchema>;

export const QuoteNearbyStockSchema = z.object({
  branchCode: z.string(),
  site: SiteRefSchema.nullable(),
  miles: z.number().nullable(),
  free: z.number(),
});

export const SalesQuoteLineSchema = z.object({
  seqno: z.number().int(),
  part: z.string(),
  description: z.string().nullable(),
  qty: z.number(),
  unit: z.number(),
  value: z.number(),
  /** Free stock at the quote's branch (null when not checked). */
  freeHere: z.number().nullable(),
  /** Nearest branches with free stock for this part (by BranchDistance), nearest first. */
  nearby: z.array(QuoteNearbyStockSchema),
});
export type SalesQuoteLine = z.infer<typeof SalesQuoteLineSchema>;

export const SalesQuoteDetailSchema = z.object({
  quote: SalesQuoteRowSchema,
  lines: z.array(SalesQuoteLineSchema),
  invoices: z.array(
    z.object({
      prefix: z.string(),
      document: z.string(),
      at: WallClockSchema,
      goods: z.number(),
      acct: z.string().nullable(),
      initials: z.string().nullable(),
    }),
  ),
  stock: z.object({
    checked: z.boolean(),
    /** Why the check could not run (dt_spar unreachable), or null. */
    error: z.string().nullable(),
    /** Branches searched for nearby stock, nearest first. */
    nearbyBranches: z.array(z.object({ branchCode: z.string(), site: SiteRefSchema.nullable(), miles: z.number() })), // prettier-ignore
  }),
});
export type SalesQuoteDetail = z.infer<typeof SalesQuoteDetailSchema>;

// Calls (S-M3, §3.7; D87) --------------------------------------------------------------------

export const CallBandSchema = z.enum(['green', 'amber', 'red', 'none']);
export type CallBand = z.infer<typeof CallBandSchema>;

export const CallFiguresSchema = z.object({
  label: z.string(),
  from: IsoDate.nullable(),
  to: IsoDate.nullable(),
  days: z.number().int(),
  inbound: z.number().int(),
  answered: z.number().int(),
  missed: z.number().int(),
  outbound: z.number().int(),
  answerPct: z.number().nullable(),
  band: CallBandSchema,
  /** Average seconds to answer (inbound, answered). */
  answerSeconds: z.number().nullable(),
  talkSeconds: z.number().int(),
  /** The same figures over the comparable prior window (null when none). */
  prior: z
    .object({
      inbound: z.number().int(),
      answered: z.number().int(),
      missed: z.number().int(),
      answerPct: z.number().nullable(),
      answerSeconds: z.number().nullable(),
    })
    .nullable(),
});
export type CallFigures = z.infer<typeof CallFiguresSchema>;

export const SalesCallsSchema = z.object({
  site: SiteRefSchema,
  today: IsoDate,
  /** The last day the old rollup covers (its `covers_to`); null before the first calls-sync. */
  coversTo: IsoDate.nullable(),
  stale: z.boolean(),
  /** The branch has call rows at all (a PBX key linked to its MamCode). */
  linked: z.boolean(),
  yesterday: CallFiguresSchema,
  month: CallFiguresSchema,
  days30: CallFiguresSchema,
  /** The last 90 days, one point per day with a rollup row. */
  trend: z.array(
    z.object({
      date: IsoDate,
      inbound: z.number().int(),
      answered: z.number().int(),
      missed: z.number().int(),
      answerPct: z.number().nullable(),
      answerSeconds: z.number().nullable(),
    }),
  ),
  bands: z.object({ greenPct: z.number(), amberPct: z.number() }),
});
export type SalesCalls = z.infer<typeof SalesCallsSchema>;

// Promotions on the branch page (S-M3, §4.6; D87) ---------------------------------------------

export const SalesPromotionSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  excerpt: z.string(),
  publishedAt: Instant,
  expireAt: Instant.nullable(),
  hasImage: z.boolean(),
  attachment: z.object({ id: z.uuid(), filename: z.string(), mime: z.string() }).nullable(),
  image: z.object({ id: z.uuid(), filename: z.string(), mime: z.string() }).nullable(),
});
export type SalesPromotion = z.infer<typeof SalesPromotionSchema>;

export const SalesPromotionsSchema = z.object({
  site: SiteRefSchema,
  items: z.array(SalesPromotionSchema),
});
export type SalesPromotions = z.infer<typeof SalesPromotionsSchema>;

// Home (S-M3): "My sales", "Quotes to chase", promotions ---------------------------------------

export const SalesMySalesSiteSchema = z.object({
  site: SiteRefSchema,
  initials: z.array(z.string()),
  today: z.object({ netSales: z.number(), invoices: z.number().int() }),
  month: z.object({ netSales: z.number(), invoices: z.number().int(), marginPct: z.number().nullable() }), // prettier-ignore
  openQuotes: z.object({ count: z.number().int(), goods: z.number(), oldestAt: WallClockSchema.nullable() }), // prettier-ignore
  /** The person's open quotes, oldest first (up to five). */
  quotes: z.array(SalesQuoteRowSchema),
  /** The person may open the branch's quotes tab. */
  canOpenBranch: z.boolean(),
});
export type SalesMySalesSite = z.infer<typeof SalesMySalesSiteSchema>;

export const SalesMySalesSchema = z.object({
  period: Period,
  /** No `autopart_inits` identity: the card says "ask your BM to link your initials". */
  linked: z.boolean(),
  sites: z.array(SalesMySalesSiteSchema),
});
export type SalesMySales = z.infer<typeof SalesMySalesSchema>;

export const SalesQuotesToChaseSchema = z.object({
  site: SiteRefSchema,
  open: z.number().int(),
  openGoods: z.number(),
  chase: z.number().int(),
  chaseGoods: z.number(),
  /** The three oldest quotes to chase, with live stock. */
  oldest: z.array(SalesQuoteRowSchema),
  thresholds: z.object({ chaseDays: z.number().int(), chaseValue: z.number() }),
});
export type SalesQuotesToChase = z.infer<typeof SalesQuotesToChaseSchema>;

export const SalesHomeResponseSchema = z.object({
  branches: z.array(SalesHomeBranchSchema),
  /** 'seat' for a BM/ABM (one card), 'region' for an RM (compact row). */
  layout: z.enum(['seat', 'region']),
  /** `sales.own.view`: own figures by header initials; null when the permission is not held. */
  mySales: SalesMySalesSchema.nullable(),
  /** One per bm/abm seat branch (empty when the person holds none). */
  quotesToChase: z.array(SalesQuotesToChaseSchema),
  /** Live promotions for the person's branch(es). */
  promotions: z.array(SalesPromotionSchema),
});
export type SalesHomeResponse = z.infer<typeof SalesHomeResponseSchema>;

// Region and Company league (§4.2) ----------------------------------------------------

export const SalesLeagueRowSchema = z.object({
  site: SiteRefSchema,
  region: RegionRef,
  /** Month-to-date (or the whole month for a closed period) net sales. */
  netSales: z.number(),
  invoices: z.number().int(),
  monthTarget: z.number().nullable(),
  targetInherited: z.boolean(),
  targetToDate: z.number().nullable(),
  pacePct: z.number().nullable(),
  paceBand: SalesBandSchema,
  projection: z.number().nullable(),
  projectionPct: z.number().nullable(),
  projectionBand: SalesBandSchema,
  runRate: z.number().nullable(),
  marginPct: z.number().nullable(),
  marginTargetPct: z.number().nullable(),
  marginBand: SalesBandSchema,
  /** Credits as a % of sales (positive). */
  creditsPct: z.number().nullable(),
  /** Net sales over the same days of the prior period, and the change against it. */
  priorNetSales: z.number().nullable(),
  changePct: z.number().nullable(),
  /** Trading days so far this period below the "behind" pace band. */
  daysOffTrack: z.number().int(),
  tradingDays: z.number().int(),
  /** S-M3: open quotes now, and the period's cohort converted within 7 days (null = no quotes). */
  openQuotes: z.number().int(),
  openQuoteGoods: z.number(),
  quotesInPeriod: z.number().int(),
  conversionPct: z.number().nullable(),
  /** S-M3: inbound calls answered over the period's days with a rollup (null = none linked). */
  callAnswerPct: z.number().nullable(),
  callBand: z.enum(['green', 'amber', 'red', 'none']),
});
export type SalesLeagueRow = z.infer<typeof SalesLeagueRowSchema>;

export const SalesLeagueTotalSchema = z.object({
  label: z.string(),
  region: RegionRef.nullable(),
  branches: z.number().int(),
  netSales: z.number(),
  invoices: z.number().int(),
  targetToDate: z.number().nullable(),
  monthTarget: z.number().nullable(),
  pacePct: z.number().nullable(),
  paceBand: SalesBandSchema,
  marginPct: z.number().nullable(),
  creditsPct: z.number().nullable(),
  priorNetSales: z.number().nullable(),
  changePct: z.number().nullable(),
});
export type SalesLeagueTotal = z.infer<typeof SalesLeagueTotalSchema>;

export const SalesLeagueSchema = z.object({
  period: Period,
  today: IsoDate,
  /** Null for the company view. */
  region: RegionRef.nullable(),
  /** Regions the caller may open (the region picker). */
  regions: z.array(RegionRef),
  /** The caller may open the company view (every branch). */
  company: z.boolean(),
  asOf: WallClockSchema.nullable(),
  rows: z.array(SalesLeagueRowSchema),
  totals: z.array(SalesLeagueTotalSchema),
  /** Invoices on codes that are not a current branch this period (company view only). */
  remainder: z.object({ netSales: z.number(), invoices: z.number().int(), codes: z.array(z.string()) }).nullable(), // prettier-ignore
  /** Twelve months of net sales per region (the region view: just that region). */
  trend: z.object({
    periods: z.array(Period),
    regions: z.array(z.object({ region: RegionRef, netSales: z.array(z.number().nullable()) })),
  }),
  /** Where the period sits: this month (live), a closed month, or a future one. */
  status: z.enum(['current', 'closed', 'future']),
});
export type SalesLeague = z.infer<typeof SalesLeagueSchema>;

// Branch › Customers lenses (§4.1) -------------------------------------------------------

export const SalesCustomerLensRowSchema = z.object({
  acct: z.string(),
  name: z.string().nullable(),
  homeSite: SiteRefSchema.nullable(),
  manager: PersonRefSchema.nullable(),
  lastInvoiceDate: IsoDate.nullable(),
  /** 90-day net sales at this branch. */
  netSales90: z.number(),
  invoices90: z.number().int(),
  /** Share of the branch's 90-day net sales (top lens). */
  share: z.number().nullable(),
  /** Today's net sales at this branch (served-today lens). */
  netSalesToday: z.number(),
  /** 90-day credits and the ratio (returns lens). */
  credits90: z.number(),
  returnsPct: z.number().nullable(),
  signals: z.array(
    z.object({
      kind: z.enum(['lapsed', 'drifting', 'high_returns']),
      value: z.number(),
      baseline: z.number(),
      since: IsoDate,
    }),
  ),
});
export type SalesCustomerLensRow = z.infer<typeof SalesCustomerLensRowSchema>;

export const SalesBranchCustomersSchema = z.object({
  site: SiteRefSchema,
  today: IsoDate,
  toCall: z.array(SalesCustomerLensRowSchema),
  top: z.array(SalesCustomerLensRowSchema),
  /** Share of the branch's 90-day net sales held by its top 10 accounts. */
  topTenShare: z.number().nullable(),
  returns: z.array(SalesCustomerLensRowSchema),
  servedToday: z.array(SalesCustomerLensRowSchema),
  rules: z.object({
    lapsed: z.record(z.string(), z.number()),
    drifting: z.record(z.string(), z.number()),
    highReturns: z.record(z.string(), z.number()),
  }),
});
export type SalesBranchCustomers = z.infer<typeof SalesBranchCustomersSchema>;
